import React from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  styled
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  SwapHoriz as SwapHorizIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { cadenceLabel } from '../../utils/generateOccurrences';

const ActionTypeIcon = ({ type, ...props }) => {
  switch (type) {
    case 'income':
      return <TrendingUpIcon {...props} sx={{ color: '#43a047' }} />;
    case 'expense':
      return <TrendingDownIcon {...props} sx={{ color: '#F41468' }} />;
    case 'transfer':
      return <SwapHorizIcon {...props} sx={{ color: '#1976d2' }} />;
    default:
      return null;
  }
};

const FrequencyChip = ({ frequency, interval }) => {
  return (
    <Chip
      size="small"
      label={cadenceLabel(frequency, interval)}
      sx={{
        bgcolor: frequency === 'once' ? '#f9fafb' : '#ffe6ef',
        color: frequency === 'once' ? '#374151' : '#F41468',
        fontWeight: 500,
        fontSize: '0.875rem',
        borderRadius: 1.5,
        height: 26
      }}
    />
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString();
};

const getAccountName = (accounts, accountId) => {
  const account = accounts.find(acc => acc.id === accountId);
  return account ? account.name : 'Unknown Account';
};

// Styled table cells similar to budgets
const BorderedHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  fontWeight: 700,
  fontSize: '0.8125rem',
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': { borderRight: 'none' }
}));
const BorderedCell = styled(TableCell)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  fontSize: '0.875rem',
  '&:last-of-type': { borderRight: 'none' }
}));

const ActionsTable = ({ actions, accounts, onEdit, onDelete, onComplete, loading, onCreate, page, rowsPerPage, onChangePage, onChangeRowsPerPage }) => {
  if (loading) {
    return <Box sx={{ display:'flex', justifyContent:'center', p:4 }}><CircularProgress /></Box>;
  }
  const empty = !actions || actions.length === 0;
  const paginated = actions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return (
    <Paper sx={{ width:'100%', overflow:'hidden', borderRadius:2 }}>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', px:2.5, py:2, borderBottom:'1px solid', borderColor:'divider' }}>
        <Typography variant="h6" fontWeight={600} sx={{ letterSpacing:0.25 }}>Planned Actions</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate} size="small">Add Action</Button>
      </Box>
      {empty ? (
        <Box sx={{ textAlign:'center', py:6 }}>
          <Typography variant="body1" fontWeight={500} sx={{ mb:1 }}>No actions found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>Create your first planned action</Typography>
          <Button variant="contained" onClick={onCreate} startIcon={<AddIcon />}>Create Action</Button>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <BorderedHeaderCell>Name</BorderedHeaderCell>
                  <BorderedHeaderCell>Amount</BorderedHeaderCell>
                  <BorderedHeaderCell>Type</BorderedHeaderCell>
                  <BorderedHeaderCell>Source</BorderedHeaderCell>
                  <BorderedHeaderCell>Destination</BorderedHeaderCell>
                  <BorderedHeaderCell>Frequency</BorderedHeaderCell>
                  <BorderedHeaderCell>Start</BorderedHeaderCell>
                  <BorderedHeaderCell>Status</BorderedHeaderCell>
                  <BorderedHeaderCell align="center">Actions</BorderedHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map(action => (
                  <TableRow key={action.id || action._id} hover sx={{ opacity: action.isActive ? 1 : 0.55 }}>
                    <BorderedCell>
                      <Typography variant="body2" fontWeight={500}>{action.name}</Typography>
                      {action.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display:'block', maxWidth:220, whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden' }}>{action.description}</Typography>
                      )}
                    </BorderedCell>
                    <BorderedCell>
                      <Typography variant="body2" fontWeight={600} color={action.type==='income' ? 'success.main' : action.type==='expense' ? 'error.main' : 'info.main'}>
                        {action.type === 'expense' ? '-' : action.type === 'income' ? '+' : ''}{formatCurrency(Math.abs(action.amount))}
                      </Typography>
                    </BorderedCell>
                    <BorderedCell>
                      <Chip size="small" label={action.type.charAt(0).toUpperCase()+action.type.slice(1)} sx={{ bgcolor: action.type==='income' ? 'success.light' : action.type==='expense' ? 'error.light' : 'info.light', color: action.type==='income' ? 'success.dark' : action.type==='expense' ? 'error.dark' : 'info.dark', fontSize:'0.75rem', fontWeight:500 }} />
                    </BorderedCell>
                    <BorderedCell>{getAccountName(accounts, action.sourceAccountId)}</BorderedCell>
                    <BorderedCell>{action.destinationAccountId ? getAccountName(accounts, action.destinationAccountId) : '—'}</BorderedCell>
                    <BorderedCell><FrequencyChip frequency={action.frequency} interval={action.interval} /></BorderedCell>
                    <BorderedCell>{formatDate(action.startDate)}</BorderedCell>
                    <BorderedCell>
                      <Chip size="small" label={action.isActive ? 'Active' : 'Inactive'} sx={{ bgcolor: action.isActive ? 'success.light' : 'grey.200', color: action.isActive ? 'success.dark' : 'text.secondary', fontSize:'0.75rem', fontWeight:600 }} />
                    </BorderedCell>
                    <BorderedCell align="center">
                      {action.frequency === 'once' && !action.isCompleted && (
                        <IconButton size="small" color="success" onClick={()=>onComplete(action)} title="Mark complete" sx={{ mr:0.5 }}>
                          <CheckIcon fontSize="inherit" />
                        </IconButton>
                      )}
                      <IconButton size="small" color="primary" onClick={()=>onEdit(action)} sx={{ mr:0.5 }}><EditIcon fontSize="inherit" /></IconButton>
                      <IconButton size="small" color="error" onClick={()=>onDelete(action)}><DeleteIcon fontSize="inherit" /></IconButton>
                    </BorderedCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={actions.length}
            page={page}
            onPageChange={onChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onChangeRowsPerPage}
            rowsPerPageOptions={[10,25,50]}
          />
        </>
      )}
    </Paper>
  );
};

export default ActionsTable; 